<template>
  <AutoDialogDrawer placement="centre" modal dismissible @close="$emit('close')">
    <template #title>{{ translate('Claim Domain Name') }}</template>
    <p v-if="language.startsWith('nl')">
      Om de domeinnaam <strong>{{ domainVerification.domain }}</strong> te claimen, vul de
      verificatiecode in die naar <strong>{{ domainVerification.sentTo }}</strong> gestuurd is op
      {{ displayTimeStamp(domainVerification.created) }}.
    </p>
    <p v-else>
      To claim the domain name <strong>{{ domainVerification.domain }}</strong
      >, enter the verification code sent to <strong>{{ domainVerification.sentTo }}</strong> on
      {{ displayTimeStamp(domainVerification.created) }}.
    </p>
    <BootstrapFormControl
      v-model="verificationCode"
      :placeholder="translate('Verification code')"
      name="verification-code"
      only-input
      class="field"
      size="md"
      :disabled="isSubmitting"
      :server-error="!!verificationCodeError"
      :error="verificationCodeError"
      required
      @enter="submit"
    />

    <template v-if="otherErrors.length > 0">
      <h5>Errors:</h5>
      <p v-for="(error, index) in otherErrors" :key="index" class="text-danger">{{ error }}</p>
    </template>

    <template #buttons>
      <BootstrapButton variant="secondary" name="cancel" @click="$emit('close')">
        {{ translate('Cancel') }}
      </BootstrapButton>
      <BootstrapButton variant="primary" name="claim" :disabled="!canSubmit" @click="submit">
        {{ translate('Claim') }}
      </BootstrapButton>
    </template>
  </AutoDialogDrawer>
</template>

<script setup lang="ts">
import {DomainVerification} from '@/api/apolloPlatform/loginPolicy';
import {errorResponseToErrorMap} from '@/sphere/error';

const {translate, displayTimeStamp, language} = useLocalisation();

const loginPolicyStore = useLoginPolicyStore();

const verificationCode = ref('');
const isSubmitting = ref(false);
const canSubmit = computed(() => verificationCode.value !== '' && !isSubmitting.value);

const errors = ref<{[index: string]: string[]}>({});
const verificationCodeError = computed(() =>
  errors.value.verification_code ? errors.value.verification_code.join(', ') : undefined,
);
const otherErrors = computed(() => {
  const errorEntries = Object.entries(errors.value).filter(
    ([key, _]) => key != 'verification_code',
  );
  errorEntries.sort(([key1, _1], [key2, _2]) => key1.localeCompare(key2));
  return errorEntries.flatMap(([_, value]) => value);
});

async function submit() {
  isSubmitting.value = true;
  try {
    errors.value = {};
    await loginPolicyStore.claimDomain(props.domainVerification.id, verificationCode.value);
  } catch (error) {
    errors.value = errorResponseToErrorMap(error);
  } finally {
    isSubmitting.value = false;
  }
}

const props = defineProps<{domainVerification: DomainVerification}>();
defineEmits<{close: []}>();
</script>
<style lang="scss" scoped>
.field {
  margin-bottom: 1rem;
}
</style>
